import { createAsyncThunk } from '@reduxjs/toolkit';

import { modelosDocumentoService } from './services';
import {
  type CreateModeloPayload,
  type ListModelosParams,
  type ListModelosResult,
  type ModeloDetail,
  type ModeloImpacto,
  type ModeloRenderResult,
  type ModeloSummary,
  type ModeloVariavel,
  type ModeloVersaoSnapshot,
  type PreviewModeloPayload,
  type PublishVersaoPayload,
  type UpdateModeloDadosPayload,
} from '../types/modelosDocumentoTypes';

const toMessage = (error: unknown, fallback: string): string =>
  error instanceof Error ? error.message : fallback;

export const listarModelos = createAsyncThunk<
  ListModelosResult,
  ListModelosParams,
  { rejectValue: string }
>('modelosDocumento/listar', async (params, thunkAPI) => {
  try {
    return await modelosDocumentoService.list(params);
  } catch (error) {
    return thunkAPI.rejectWithValue(toMessage(error, 'Erro ao carregar os modelos'));
  }
});

export const fetchCatalogoVariaveis = createAsyncThunk<
  ModeloVariavel[],
  void,
  { rejectValue: string }
>('modelosDocumento/catalogoVariaveis', async (_arg, thunkAPI) => {
  try {
    return await modelosDocumentoService.listarCatalogoVariaveis();
  } catch (error) {
    return thunkAPI.rejectWithValue(toMessage(error, 'Erro ao carregar o catálogo de variáveis'));
  }
});

export const buscarModeloDetalhe = createAsyncThunk<ModeloDetail, string, { rejectValue: string }>(
  'modelosDocumento/detalhe',
  async (id, thunkAPI) => {
    try {
      return await modelosDocumentoService.detail(id);
    } catch (error) {
      return thunkAPI.rejectWithValue(toMessage(error, 'Erro ao carregar o modelo'));
    }
  },
);

export const criarModelo = createAsyncThunk<
  ModeloDetail,
  CreateModeloPayload,
  { rejectValue: string }
>('modelosDocumento/criar', async (payload, thunkAPI) => {
  try {
    return await modelosDocumentoService.create(payload);
  } catch (error) {
    return thunkAPI.rejectWithValue(toMessage(error, 'Erro ao criar o modelo'));
  }
});

export const publicarVersaoModelo = createAsyncThunk<
  ModeloVersaoSnapshot,
  { id: string; payload: PublishVersaoPayload },
  { rejectValue: string }
>('modelosDocumento/publicarVersao', async ({ id, payload }, thunkAPI) => {
  try {
    return await modelosDocumentoService.publishVersao(id, payload);
  } catch (error) {
    return thunkAPI.rejectWithValue(toMessage(error, 'Erro ao publicar a nova versão'));
  }
});

export const atualizarDadosModelo = createAsyncThunk<
  ModeloDetail,
  { id: string; payload: UpdateModeloDadosPayload },
  { rejectValue: string }
>('modelosDocumento/atualizarDados', async ({ id, payload }, thunkAPI) => {
  try {
    return await modelosDocumentoService.updateDados(id, payload);
  } catch (error) {
    return thunkAPI.rejectWithValue(
      toMessage(error, 'Erro ao atualizar o nome/categoria do modelo'),
    );
  }
});

export const alternarAtivoModelo = createAsyncThunk<
  ModeloSummary,
  { id: string; ativo: boolean },
  { rejectValue: string }
>('modelosDocumento/alternarAtivo', async ({ id, ativo }, thunkAPI) => {
  try {
    return await modelosDocumentoService.setAtivo(id, ativo);
  } catch (error) {
    return thunkAPI.rejectWithValue(toMessage(error, 'Erro ao atualizar o status do modelo'));
  }
});

export const verificarImpactoModelo = createAsyncThunk<
  ModeloImpacto,
  string,
  { rejectValue: string }
>('modelosDocumento/impacto', async (id, thunkAPI) => {
  try {
    return await modelosDocumentoService.impacto(id);
  } catch (error) {
    return thunkAPI.rejectWithValue(toMessage(error, 'Erro ao verificar o impacto do modelo'));
  }
});

export const previewModelo = createAsyncThunk<
  ModeloRenderResult,
  PreviewModeloPayload,
  { rejectValue: string }
>('modelosDocumento/preview', async (payload, thunkAPI) => {
  try {
    return await modelosDocumentoService.preview(payload);
  } catch (error) {
    return thunkAPI.rejectWithValue(toMessage(error, 'Erro ao gerar a pré-visualização'));
  }
});
